import useGetFeatureFlagsByTenantID from "@/features/global-admin/hooks/useGetFeatureFlagsByTenantID";
import useAuthenticatedUser from "@/hooks/useAuthenticatedUser";
import LoadingSpinner from "@/ui-lib/components/LoadingSpinner";
import { LabeledSwitch } from "@/ui-lib/components/Switch";
import { useTheme } from "@emotion/react";
import { FeatureFlag } from "@ternary/api-lib/constants/feature-flags";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Box from "@ternary/web-ui-lib/components/Box";
import Flex from "@ternary/web-ui-lib/components/Flex";
import Text from "@ternary/web-ui-lib/components/Text";
import React, { useMemo } from "react";
import copyText from "../copyText";
import useLocalFeatureFlagOverrides from "../hooks/useLocalFeatureFlagOverrides";

export default function FeatureFlagOverrideContainer() {
  const authenticatedUser = useAuthenticatedUser();
  const theme = useTheme();

  const { localFeatureFlagOverrides, setLocalFeatureFlagOverride } =
    useLocalFeatureFlagOverrides();

  //
  // Queries
  //
  const { data: featureFlags, isLoading } = useGetFeatureFlagsByTenantID(
    authenticatedUser.tenant.id
  );

  //
  // Handlers
  //
  const handleToggle = (flag: FeatureFlag, checked: boolean) => {
    setLocalFeatureFlagOverride(flag, checked);
  };

  const handleReset = (flag: FeatureFlag) => {
    setLocalFeatureFlagOverride(flag, null);
  };

  const handleResetAll = () => {
    Object.values(FeatureFlag).forEach((flag) => {
      setLocalFeatureFlagOverride(flag, null);
    });
  };

  //
  // Render
  //
  const flagsData = useMemo(() => {
    if (!featureFlags) return [];

    return Object.values(FeatureFlag).map((flag) => {
      const featureFlag = featureFlags.find((ff) => ff.flag === flag);
      const defaultValue = Boolean(featureFlag);
      const overrideValue = localFeatureFlagOverrides[flag];

      return {
        flag,
        defaultValue,
        effectiveValue: overrideValue ?? defaultValue,
      };
    });
  }, [featureFlags, localFeatureFlagOverrides]);

  return (
    <Flex direction="column" gap={theme.space_md}>
      <Box>
        <Text appearance="h2">{copyText.localFeatureFlagsTitle}</Text>
        <Text marginTop={theme.space_xxs} color="text_secondary">
          {copyText.localFeatureFlagsDescription}
        </Text>
      </Box>

      {isLoading && (
        <Flex alignItems="center" gap={theme.space_xs}>
          <LoadingSpinner />
          <Text>{copyText.loading}</Text>
        </Flex>
      )}

      {!isLoading && (
        <Flex direction="column" gap={theme.space_xxs} maxWidth="500px">
          <Text fontWeight={theme.fontWeight_semiBold}>
            {copyText.standardFlagsLabel}
          </Text>

          <Flex direction="column" gap={theme.space_xs}>
            {flagsData.map((flagData) => {
              const overrideStatus =
                flagData.defaultValue === flagData.effectiveValue
                  ? copyText.defaultStatusLabel
                  : copyText.overrideStatusLabel;

              return (
                <Flex justifyContent="space-between" alignItems="center">
                  <LabeledSwitch
                    label={flagData.flag}
                    labelSide="right"
                    checked={flagData.effectiveValue}
                    onChange={(checked) => handleToggle(flagData.flag, checked)}
                  />
                  <Flex gap={theme.space_md} alignItems="center">
                    <Text
                      fontWeight={
                        overrideStatus === "override"
                          ? theme.fontWeight_semiBold
                          : theme.fontWeight_regular
                      }
                    >
                      {overrideStatus}
                    </Text>
                    <Button
                      size="small"
                      onClick={() => handleReset(flagData.flag)}
                    >
                      {copyText.resetButtonLabel}
                    </Button>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
          <Box marginTop={theme.space_md}>
            <Button primary onClick={handleResetAll}>
              {copyText.resetAllButtonLabel}
            </Button>
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
